'use client';
import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Copy, Check, Mail, Lock, User, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

type LoginData = { email: string; password: string; remember: boolean };
type SignupData = { fullName: string; email: string; password: string; confirmPassword: string; country: string };

const PRIORITY_COUNTRIES = [
  'Zimbabwe', 'Zambia', 'Botswana', 'Namibia', 'South Africa', 'Malawi',
  'Mozambique', 'Angola', 'Tanzania', 'Lesotho', 'Swaziland', 'Rwanda', 'Kenya',
];

const ALL_COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
  'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
  'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
  'Congo (Brazzaville)', 'Congo (Kinshasa)', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus',
  'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador',
  'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini',
  'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany',
  'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq',
  'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya',
  'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho',
  'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania',
  'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro',
  'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands',
  'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia',
  'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea',
  'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland',
  'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Yemen', 'Zambia', 'Zimbabwe',
];

const OTHER_COUNTRIES = ALL_COUNTRIES.filter((c) => !PRIORITY_COUNTRIES.includes(c)).sort();

const demoCredentials = [
  { role: 'Business Owner', email: 'thabo@nexustech.co.za', password: 'Nexus@2026' },
  { role: 'Freelancer', email: 'lerato.dlamini@freelance.co.za', password: 'Lerato@2026' },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="ml-1 p-0.5 rounded transition-colors" style={{ color: 'var(--color-text-muted)' }} title="Copy">
      {copied ? <Check size={11} style={{ color: 'var(--color-primary)' }} /> : <Copy size={11} />}
    </button>
  );
}

export default function AuthForm() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signIn, signUp } = useAuth();
  const supabase = createClient();

  const getLoginRedirect = async (): Promise<string> => {
    try {
      const { data } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'login_redirect')
        .single();
      return data?.value || '/';
    } catch {
      return '/';
    }
  };

  const getRegistrationRedirect = async (): Promise<string> => {
    try {
      const { data } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'registration_redirect')
        .single();
      return data?.value || '/';
    } catch {
      return '/';
    }
  };

  const loginForm = useForm<LoginData>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupData>({ defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', country: '' } });

  const handleLogin = loginForm.handleSubmit(async (data) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Signed in successfully!');
      const redirectPath = await getLoginRedirect();
      // Small delay to allow the session cookie to be written before the hard redirect
      await new Promise((r) => setTimeout(r, 300));
      window.location.href = redirectPath;
    } catch (err: any) {
      loginForm.setError('password', {
        message: err?.message || 'Invalid credentials — check your email and password',
      });
      setLoading(false);
    }
  });

  const handleSignup = signupForm.handleSubmit(async (data) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.fullName });
      toast.success('Account created! Welcome to Cards247.');
      const redirectPath = await getRegistrationRedirect();
      await new Promise((r) => setTimeout(r, 300));
      window.location.href = redirectPath;
    } catch (err: any) {
      signupForm.setError('email', { message: err?.message || 'Could not create account. Try again.' });
      setLoading(false);
    }
  });

  const autofill = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    toast.info('Demo credentials filled — click Sign In');
  };

  return (
    <div className="w-full max-w-md">
      {/* Logo mobile */}
      <div className="flex items-center gap-2 mb-8 lg:hidden">
        <div className="w-9 h-9 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6c5ce7, #fd79a8)' }}>
          <span className="font-extrabold text-white">C</span>
        </div>
        <span className="font-extrabold text-lg tracking-tight" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}>Cards247</span>
      </div>

      {/* Mode tabs */}
      <div className="flex rounded-2xl p-1 mb-8" style={{ background: 'var(--color-primary-light)' }}>
        <button
          onClick={() => setMode('login')}
          className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${mode === 'login' ? 'grind-tab-active' : 'grind-tab-inactive'}`}
          style={{ fontFamily: 'var(--font-body)' }}
        >
          Sign In
        </button>
        <button
          onClick={() => setMode('signup')}
          className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${mode === 'signup' ? 'grind-tab-active' : 'grind-tab-inactive'}`}
          style={{ fontFamily: 'var(--font-body)' }}
        >
          Create Account
        </button>
      </div>

      {mode === 'login' ? (
        <form onSubmit={handleLogin} className="space-y-5 fade-in">
          <div>
            <p className="grind-section-label mb-1">Welcome back</p>
            <h1 className="grind-section-title" style={{ fontSize: '1.75rem' }}>Sign in to Cards247</h1>
            <p className="text-sm mt-1.5" style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Manage your business cards and page</p>
          </div>

          <div>
            <label className="grind-field-label">Email Address</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
              <input
                type="email"
                {...loginForm.register('email', { required: 'Email is required' })}
                placeholder="thabo@nexustech.co.za"
                className="grind-input"
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
            {loginForm.formState.errors.email && (
              <p className="text-xs text-red-500 mt-1.5">{loginForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="grind-field-label" style={{ marginBottom: 0 }}>Password</label>
              <button type="button" className="text-xs font-bold hover:underline" style={{ color: 'var(--color-primary)' }}>Forgot password?</button>
            </div>
            <div className="relative mt-1">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
              <input
                type={showPass ? 'text' : 'password'}
                {...loginForm.register('password', { required: 'Password is required' })}
                placeholder="••••••••"
                className="grind-input"
                style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="text-xs text-red-500 mt-1.5">{loginForm.formState.errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="remember"
              {...loginForm.register('remember')}
              className="w-4 h-4 rounded"
              style={{ accentColor: 'var(--color-primary)' }}
            />
            <label htmlFor="remember" className="text-sm" style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Remember me for 30 days</label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="grind-btn-amber w-full py-3.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in...</> : 'Sign In →'}
          </button>

          {/* Demo credentials */}
          <div className="mt-4 p-4 rounded-2xl" style={{ background: 'var(--color-primary-light)', border: '1px solid var(--color-card-border)' }}>
            <p className="grind-section-label mb-3">Demo Accounts</p>
            {demoCredentials.map((c) => (
              <div key={c.role} className="flex items-center justify-between py-2 border-b last:border-b-0" style={{ borderColor: 'var(--color-divider)' }}>
                <div>
                  <p className="text-xs font-bold" style={{ color: 'var(--color-text-primary)' }}>{c.role}</p>
                  <p className="text-xs flex items-center" style={{ color: 'var(--color-text-secondary)' }}>
                    {c.email} <CopyButton text={c.email} />
                  </p>
                  <p className="text-xs flex items-center" style={{ color: 'var(--color-text-secondary)' }}>
                    {c.password} <CopyButton text={c.password} />
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => autofill(c.email, c.password)}
                  className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                  style={{ color: 'var(--color-primary)', background: 'rgba(108,92,231,0.1)' }}
                >
                  Fill
                </button>
              </div>
            ))}
          </div>
        </form>
      ) : (
        <form onSubmit={handleSignup} className="space-y-5 fade-in">
          <div>
            <p className="grind-section-label mb-1">Get started</p>
            <h1 className="grind-section-title" style={{ fontSize: '1.75rem' }}>Create your account</h1>
            <p className="text-sm mt-1.5" style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Start building professional business cards today</p>
          </div>

          <div>
            <label className="grind-field-label">Full Name</label>
            <div className="relative">
              <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
              <input
                type="text"
                {...signupForm.register('fullName', { required: 'Full name is required' })}
                placeholder="Thabo Mokoena"
                className="grind-input"
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
            {signupForm.formState.errors.fullName && (
              <p className="text-xs text-red-500 mt-1.5">{signupForm.formState.errors.fullName.message}</p>
            )}
          </div>

          <div>
            <label className="grind-field-label">Email Address</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
              <input
                type="email"
                {...signupForm.register('email', { required: 'Email is required' })}
                placeholder="you@example.com"
                className="grind-input"
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
            {signupForm.formState.errors.email && (
              <p className="text-xs text-red-500 mt-1.5">{signupForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="grind-field-label">Password</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
              <input
                type={showPass ? 'text' : 'password'}
                {...signupForm.register('password', { required: 'Password is required', minLength: { value: 6, message: 'Minimum 6 characters' } })}
                placeholder="••••••••"
                className="grind-input"
                style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {signupForm.formState.errors.password && (
              <p className="text-xs text-red-500 mt-1.5">{signupForm.formState.errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="grind-field-label">Confirm Password</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
              <input
                type={showConfirm ? 'text' : 'password'}
                {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                placeholder="••••••••"
                className="grind-input"
                style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {signupForm.formState.errors.confirmPassword && (
              <p className="text-xs text-red-500 mt-1.5">{signupForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>

          <div>
            <label className="grind-field-label">Country</label>
            <div className="relative">
              <Globe size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
              <select
                {...signupForm.register('country', { required: 'Please select your country' })}
                className="grind-input appearance-none"
                style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem', color: 'var(--color-text-primary)', background: 'var(--color-card-bg)' }}
              >
                <option value="">Select your country</option>
                <optgroup label="── Africa ──">
                  {PRIORITY_COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </optgroup>
                <optgroup label="── Rest of the World ──">
                  {OTHER_COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </optgroup>
              </select>
              <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--color-text-muted)' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
            </div>
            {signupForm.formState.errors.country && (
              <p className="text-xs text-red-500 mt-1.5">{signupForm.formState.errors.country.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="grind-btn-amber w-full py-3.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Creating account...</> : 'Create Account →'}
          </button>
        </form>
      )}
    </div>
  );
}